import { Component, Input, OnInit, inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';

import { Treatment } from '@next/arable-lib/advice';
import { AppInsightsService, AppInsightsServiceInjector } from '@next/core-lib/app-insights';
import { DialogService } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { TableColumnTypes, TableConfig, TableComponent } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';

import { combineLatest, ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { selectIsUserEligibleToViewCropFieldAdvices, selectManualCropAids } from '../../store/selectors';
import { TreatmentDialogComponent } from '../treatment-dialog/treatment-dialog.component';

@Component({
  selector: 'app-cropfield-treatments',
  templateUrl: './cropfield-treatments.component.html',
  imports: [TableComponent],
})
export class CropFieldTreatmentsComponent implements OnInit {
  private readonly locale = inject(LocaleService);
  private readonly dialog = inject(DialogService);
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly ai = inject<AppInsightsService>(AppInsightsServiceInjector);

  @Input() treatments: Treatment[] = [];
  public cropFieldTreatmentsTableConfig!: TableConfig;
  private readonly destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  ngOnInit(): void {
    const cropAids$ = this.store.pipe(select(selectManualCropAids), takeUntil(this.destroyed$));
    const user$ = this.store.pipe(select(UserSelectors.selectUser), takeUntil(this.destroyed$));
    const customer$ = this.store.pipe(
      select(UserSelectors.selectCustomer),
      takeUntil(this.destroyed$),
    );

    const isUserEligibleToViewCropFieldAdvices$ = this.store.select(selectIsUserEligibleToViewCropFieldAdvices);

    const fertilizationObjectives = [
      { value: 'Basisgift', name: this.locale.translate('basegift') },
      { value: 'basegift', name: this.locale.translate('basegift') },
      { value: 'extraFertilization', name: this.locale.translate('extraFertilization') },
      { value: 'leafFertilization', name: this.locale.translate('leafFertilization') },
      { value: 'fertigation', name: this.locale.translate('fertigation') },
    ];

    this.treatments.forEach((x) => {
      const objective = fertilizationObjectives.find((fo) => fo.value === x.objective);
      if (objective) {
        x.objective = objective.name;
      }
    });

    const combined = combineLatest([cropAids$, user$, customer$, isUserEligibleToViewCropFieldAdvices$]);
    combined.subscribe(([cropAids, user, customer, isAllowedToViewTreatmentDetails]) => {
      this.cropFieldTreatmentsTableConfig = {
        dataSource: new MatTableDataSource<Treatment>(this.treatments) as unknown as MatTableDataSource<unknown, MatPaginator>,
        columns: [
          {
            id: 'name',
            type: TableColumnTypes.STRING,
            label: this.locale.translate('treatment'),
          },
          {
            id: 'objective',
            type: TableColumnTypes.STRING,
            label: this.locale.translate('objective'),
          },
          {
            id: 'frequency',
            type: TableColumnTypes.NUMBER,
            label: this.locale.translate('frequency'),
          },
        ],
        actions: [],
        selectColumn: false,
        pagination: false,
        primaryColumn: 'name',
      };

      if (isAllowedToViewTreatmentDetails) {
        this.cropFieldTreatmentsTableConfig.actions?.push(
          {
            id: 'edit',
            // @ts-ignore
            callback: (treatment: Treatment) => {
              this.ai.trackEvent('openCropFieldTreatmentsDetails', {
                user: user?.email,
                customerId: customer?.id,
                customerName: customer?.name,
                treatment: (treatment as Treatment).id,
                treatmentType: (treatment as Treatment).treatmentType,
              });
              this.dialog.form(TreatmentDialogComponent, {
                treatment,
                title: treatment.name,
                cropAids,
                fullscreen: true,
                confirmButton: false,
                pushHistoryState: false,
                cancelButtonText: this.locale.translate('close'),
              });
            },
            label: '',
            icon: 'more_horiz',
            disabled: () => false,
            primary: false,
          },
        );
      }
    });
  }
}
